import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, MessageSquare, Info, TestTube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MediaPicker from "@/components/ui/media-picker";

interface NotificationTemplate {
  id?: number;
  name: string;
  description: string;
  category: string;
  type: string;
  trigger_event: string;
  title_template: string;
  body_template: string;
  action_url?: string;
  image_url?: string;
  variables?: Record<string, any>;
  target_segments?: string[];
  send_time_offset?: string;
  optimal_time?: boolean;
  frequency_limit?: string;
  is_active: boolean;
  created_by?: number;
}

const CATEGORIES = [
  { value: "onboarding", label: "Onboarding" },
  { value: "engagement", label: "Engagement" },
  { value: "subscription", label: "Subscription" },
  { value: "content", label: "Content" },
  { value: "marketing", label: "Marketing" },
  { value: "system", label: "System" },
];

const NOTIFICATION_TYPES = [
  { value: "push", label: "Push Notification" },
  { value: "email", label: "Email" },
  { value: "in_app", label: "In-App Message" },
  { value: "sms", label: "SMS" },
];

const TRIGGER_EVENTS = [
  { value: "signup", label: "User Signup" },
  { value: "subscription_start", label: "Subscription Start" },
  { value: "trial_ending", label: "Trial Ending" },
  { value: "book_finished", label: "Book Finished" },
  { value: "inactive_user", label: "Inactive User" },
  { value: "new_content", label: "New Content" },
  { value: "manual", label: "Manual Trigger" },
];

const COMMON_VARIABLES = [
  "name",
  "email",
  "subscription_plan",
  "trial_days_left",
  "book_title",
  "book_author",
  "book_id",
  "reading_streak",
  "books_read_count",
  "discount_percentage",
  "hours_left",
];

// Book-related trigger events that should automatically include book variables
const BOOK_RELATED_TRIGGERS = ["book_finished", "new_content"];

// Variables that should be automatically included for book-related templates
const BOOK_VARIABLES = ["book_id", "book_title", "book_author"];

export default function NotificationTemplateForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState<NotificationTemplate>({
    name: "",
    description: "",
    category: "onboarding",
    type: "push",
    trigger_event: "manual",
    title_template: "",
    body_template: "",
    action_url: "",
    image_url: "",
    variables: {},
    target_segments: [],
    send_time_offset: "",
    optimal_time: false,
    frequency_limit: "",
    is_active: true,
  });

  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);

  const isEditing = !!id;

  useEffect(() => {
    fetchAvailableSegments();
    if (isEditing) {
      fetchTemplate();
    }
  }, [id]);

  const fetchAvailableSegments = async () => {
    try {
      const { data, error } = await supabase
        .from("user_groups" as any)
        .select("name")
        .eq("is_active", true);

      if (error) throw error;
      setAvailableSegments(data?.map((group: any) => group.name) || []);
    } catch (error) {
      console.error("Error fetching user groups:", error);
    }
  };

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("notification_templates" as any)
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (data) {
        setFormData({
          ...data,
          variables: data.variables || {},
          target_segments: data.target_segments || [],
        });
      }
    } catch (error) {
      console.error("Error fetching template:", error);
      toast({
        title: "Error",
        description: "Failed to fetch template details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Template name is required.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.title_template.trim()) {
      toast({
        title: "Error",
        description: "Title template is required.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.body_template.trim()) {
      toast({
        title: "Error",
        description: "Body template is required.",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to create templates.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        description: formData.description || null,
        category: formData.category,
        type: formData.type,
        trigger_event: formData.trigger_event || null,
        title_template: formData.title_template,
        body_template: formData.body_template,
        action_url: formData.action_url || null,
        image_url: formData.image_url || null,
        variables: formData.variables || {},
        target_segments: formData.target_segments || [],
        send_time_offset: formData.send_time_offset || null,
        optimal_time: formData.optimal_time || false,
        frequency_limit: formData.frequency_limit || null,
        is_active: formData.is_active,
        created_by: user.id,
      };

      if (isEditing) {
        const { error } = await supabase
          .from("notification_templates" as any)
          .update(payload)
          .eq("id", id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("notification_templates" as any)
          .insert(payload);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Template ${
          isEditing ? "updated" : "created"
        } successfully.`,
      });

      navigate("/admin/notifications/templates");
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} template.`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    const variables = {
      name: "John Doe",
      book_title: "The 7 Habits of Highly Effective People",
      book_author: "Stephen Covey",
      book_id: "123",
      trial_days_left: 3,
      books_read_count: 12,
      discount_percentage: 25,
      hours_left: 48,
    };

    let previewTitle = formData.title_template;
    let previewBody = formData.body_template;

    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, "g");
      previewTitle = previewTitle.replace(regex, String(value));
      previewBody = previewBody.replace(regex, String(value));
    });

    setPreviewData({
      title: previewTitle,
      body: previewBody,
      image_url: formData.image_url,
      variables,
    });
  };

  const addVariable = (variable: string) => {
    const newVariables = { ...formData.variables };
    newVariables[variable] = "string";
    setFormData((prev) => ({ ...prev, variables: newVariables }));
  };

  const removeVariable = (variable: string) => {
    const newVariables = { ...formData.variables };
    delete newVariables[variable];
    setFormData((prev) => ({ ...prev, variables: newVariables }));
  };

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const backButton = (
    <Button
      variant="ghost"
      onClick={() => navigate("/admin/notifications/templates")}
    >
      <ArrowLeft className="h-4 w-4 mr-2" />
      Back to Templates
    </Button>
  );

  return (
    <AdminLayout
      title={isEditing ? "Edit Template" : "Create Template"}
      actionButton={backButton}
      mainClassName="flex-1 overflow-y-auto p-6 bg-background max-w-4xl mx-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Configure the basic details of your notification template
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Welcome New User"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, category: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Describe the purpose of this notification template..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Notification Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTIFICATION_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="trigger_event">Trigger Event *</Label>
                <Select
                  value={formData.trigger_event}
                  onValueChange={(value) => {
                    setFormData((prev) => {
                      const newFormData = { ...prev, trigger_event: value };

                      // Automatically include book variables for book-related triggers
                      if (BOOK_RELATED_TRIGGERS.includes(value)) {
                        const currentVariables = { ...prev.variables };
                        BOOK_VARIABLES.forEach((bookVar) => {
                          if (!currentVariables[bookVar]) {
                            currentVariables[bookVar] = "string";
                          }
                        });
                        newFormData.variables = currentVariables;
                      }

                      return newFormData;
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select trigger" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIGGER_EVENTS.map((event) => (
                      <SelectItem key={event.value} value={event.value}>
                        {event.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Book-related trigger info */}
            {BOOK_RELATED_TRIGGERS.includes(formData.trigger_event) && (
              <Alert className="p-4 border-l-4 border-l-blue-500 bg-blue-50 dark:bg-blue-950/20 dark:border-l-blue-400">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <AlertDescription className="text-blue-800 dark:text-blue-200 font-medium">
                      Book-Related Template
                    </AlertDescription>
                    <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                      <p>
                        • Book variables (book_id, book_title, book_author) have
                        been automatically added
                      </p>
                      <p>
                        • These variables will be populated with actual book
                        data when notifications are sent
                      </p>
                      <p>
                        • You can use these variables in your title and body
                        templates: {`{{book_id}}`}, {`{{book_title}}`},{" "}
                        {`{{book_author}}`}
                      </p>
                    </div>
                  </div>
                </div>
              </Alert>
            )}

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="is_active">Active</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Active templates can be used for notifications
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, is_active: checked }))
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Message Content */}
        <Card>
          <CardHeader>
            <CardTitle>Message Content</CardTitle>
            <CardDescription>
              Design the notification message using variables for
              personalization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Use variables like {`{{name}}`}, {`{{book_title}}`}, etc. to
                personalize messages. Variables will be replaced with actual
                values when sent.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="title_template">Title Template *</Label>
              <Input
                id="title_template"
                value={formData.title_template}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    title_template: e.target.value,
                  }))
                }
                placeholder="e.g., Welcome {{name}}!"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="body_template">Body Template *</Label>
              <Textarea
                id="body_template"
                value={formData.body_template}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    body_template: e.target.value,
                  }))
                }
                placeholder="e.g., Hi {{name}}! Welcome to our platform. Start reading {{book_title}} now!"
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="action_url">Action URL</Label>
              <Input
                id="action_url"
                value={formData.action_url}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    action_url: e.target.value,
                  }))
                }
                placeholder="e.g., /books/{{book_id}}, /dashboard"
              />
            </div>

            <div className="space-y-2">
              <Label>Image URL (Optional)</Label>
              <MediaPicker
                onSelect={(url) =>
                  setFormData((prev) => ({
                    ...prev,
                    image_url: url,
                  }))
                }
                currentValue={formData.image_url || ""}
                allowedTypes={[
                  "image/jpeg",
                  "image/jpg",
                  "image/png",
                  "image/webp",
                ]}
                title="Select Template Image"
                description="Choose an image from your media library, upload a new one, or use an external URL"
                buttonText="Select Image"
                showPreview={true}
              />
              <p className="text-xs text-muted-foreground">
                Optional image to display with the notification. Use HTTPS URLs
                for best compatibility.
              </p>
            </div>

            {/* Common Variables */}
            <div className="space-y-2">
              <Label>Common Variables</Label>
              <div className="flex flex-wrap gap-2">
                {COMMON_VARIABLES.map((variable) => (
                  <Badge
                    key={variable}
                    variant={
                      formData.variables?.[variable] ? "default" : "outline"
                    }
                    className="cursor-pointer"
                    onClick={() => {
                      if (formData.variables?.[variable]) {
                        removeVariable(variable);
                      } else {
                        addVariable(variable);
                      }
                    }}
                  >
                    {variable}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="flex gap-4 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handlePreview}>
                <TestTube className="h-4 w-4 mr-2" />
                Preview Message
              </Button>
            </div>

            {previewData && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2">Preview:</h4>
                <div className="space-y-2">
                  <div>
                    <strong>Title:</strong> {previewData.title}
                  </div>
                  <div>
                    <strong>Body:</strong> {previewData.body}
                  </div>
                  {previewData.image_url && (
                    <div>
                      <strong>Image:</strong>{" "}
                      <a
                        href={previewData.image_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {previewData.image_url}
                      </a>
                    </div>
                  )}
                  <div className="text-sm text-gray-600">
                    <strong>Sample data used:</strong>{" "}
                    {JSON.stringify(previewData.variables, null, 2)}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Advanced Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Advanced Settings</CardTitle>
            <CardDescription>
              Configure timing and targeting options
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="send_time_offset">Send Time Offset</Label>
                <Input
                  id="send_time_offset"
                  value={formData.send_time_offset}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      send_time_offset: e.target.value,
                    }))
                  }
                  placeholder="e.g., 5 minutes, 1 hour, 2 days"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="frequency_limit">Frequency Limit</Label>
                <Input
                  id="frequency_limit"
                  value={formData.frequency_limit}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      frequency_limit: e.target.value,
                    }))
                  }
                  placeholder="e.g., once_per_day, once_per_week"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="optimal_time">Optimal Time Delivery</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Send at the optimal time for each user
                </p>
              </div>
              <Switch
                id="optimal_time"
                checked={formData.optimal_time}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, optimal_time: checked }))
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/notifications/templates")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading
              ? "Saving..."
              : isEditing
              ? "Update Template"
              : "Create Template"}
          </Button>
        </div>
      </form>
    </AdminLayout>
  );
}
